import React, { useState } from 'react';
import { CaseCategory, CreateSupportCaseRequest } from '../types/support-case';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { useToast } from './ui/use-toast';

interface SupportCaseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSupportCaseRequest) => Promise<void>;
  isLoading?: boolean;
}

const SupportCaseForm: React.FC<SupportCaseFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<CreateSupportCaseRequest>({
    caseCategory: 'question',
    title: '',
    description: '',
  });

  const [errors, setErrors] = useState<Partial<CreateSupportCaseRequest>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<CreateSupportCaseRequest> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length > 200) {
      newErrors.title = 'Title cannot exceed 200 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length > 2000) {
      newErrors.description = 'Description cannot exceed 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        status: "error",
      });
      return;
    }

    try {
      await onSubmit(formData);
      handleClose();
      toast({
        title: "Success",
        description: "Support case created successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create support case. Please try again.",
        status: "error",
      });
    }
  };

  const handleClose = () => {
    setFormData({
      caseCategory: 'question',
      title: '',
      description: '',
    });
    setErrors({});
    onClose();
  };

  const handleInputChange = (
    field: keyof CreateSupportCaseRequest,
    value: any
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const getCategoryLabel = (category: CaseCategory): string => {
    switch (category) {
      case 'issue':
        return 'Issue';
      case 'question':
        return 'Question';
      case 'feature_request':
        return 'Feature Request';
      case 'feedback':
        return 'Feedback';
      default:
        return category;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create Support Case</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="caseCategory">Category *</Label>
            <Select
              value={formData.caseCategory}
              onValueChange={(value: CaseCategory) => handleInputChange('caseCategory', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="issue">Issue</SelectItem>
                <SelectItem value="question">Question</SelectItem>
                <SelectItem value="feature_request">Feature Request</SelectItem>
                <SelectItem value="feedback">Feedback</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Brief title for your support case"
              className={errors.title ? 'border-red-500' : ''}
            />
            {errors.title && (
              <p className="text-sm text-red-500">{errors.title}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Please provide detailed information about your issue, question, feature request, or feedback..."
              rows={6}
              className={errors.description ? 'border-red-500' : ''}
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description}</p>
            )}
            <p className="text-sm text-gray-500">
              {formData.description.length}/2000 characters
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Case'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupportCaseForm; 